/**
 * Os planos TM+, como o servidor os manda.
 *
 * lume.php emite window.__LUME_PLANS__ no HTML — os planos activos da base de
 * dados, com a campanha do AutoCoupons já resolvida. É a mesma fonte que o
 * checkout e a landing page usam, e é por isso que existe: enquanto os preços
 * estiveram escritos no bundle, mudá-los no checkout deixava o Lume a anunciar
 * outra coisa.
 *
 * NADA AQUI É GARANTIDO. A lista pode vir vazia (uma consulta que falhou) e o
 * atributo pode nem existir (uma tela renderizada fora do lume.php, um teste).
 * Quem lê trata a ausência como um caso normal e cai na sua própria tabela de
 * recurso — ver conversion-modal-copy.ts.
 */

export interface LumePlan {
  id: number;
  /** Meses. 1, 3 ou 12 — é por aqui que a modal casa o plano com a sua cópia. */
  duration: number;
  name: string;
  /** O preço de tabela, em número. */
  value: number;
  /** O preço de tabela, formatado pelo servidor. */
  priceLabel: string;
  /** O cupão automático a decorrer neste plano, se houver. */
  coupon: string | null;
  percentOff: number | null;
  promoValue: number | null;
  promoLabel: string | null;
}

function isPlan(value: unknown): value is LumePlan {
  if (typeof value !== "object" || value === null) return false;

  const plan = value as Record<string, unknown>;

  return (
    typeof plan.id === "number" &&
    typeof plan.duration === "number" &&
    typeof plan.value === "number" &&
    typeof plan.priceLabel === "string"
  );
}

/** Os planos activos, ou uma lista vazia se o servidor não os mandou. */
export function serverPlans(): LumePlan[] {
  const found = (window as unknown as Record<string, unknown>).__LUME_PLANS__;

  return Array.isArray(found) ? found.filter(isPlan) : [];
}

/** O plano de N meses, ou null. */
export function planByDuration(duration: number): LumePlan | null {
  return serverPlans().find((plan) => plan.duration === duration) ?? null;
}

/** A campanha do Mensal, como os banners a anunciam. */
export interface CampaignOffer {
  /** Já arredondada: "33", nunca "33.33". */
  percentOff: number;
  /** O preço com desconto, formatado pelo servidor. */
  price: string;
  /** O preço de tabela, riscado. */
  was: string;
}

/**
 * A oferta dos banners, ou null fora da campanha.
 *
 * SÓ O MENSAL: é o plano da campanha e o único preço que os banners dizem. A
 * data de fim é a do cupão na base de dados — quando ele expira o servidor deixa
 * de mandar promoValue e a oferta desaparece sem deploy.
 */
export function campaignOffer(): CampaignOffer | null {
  const monthly = planByDuration(1);

  if (
    monthly?.promoValue == null ||
    monthly.percentOff == null ||
    monthly.promoLabel == null
  ) {
    return null;
  }

  return {
    percentOff: Math.round(monthly.percentOff),
    price: unbreakable(monthly.promoLabel),
    was: unbreakable(monthly.priceLabel),
  };
}

// Na fita curta do mobile o «R$» ficava no fim de uma linha e o valor na seguinte.
function unbreakable(label: string): string {
  return label.replace(/ /g, " ");
}
